import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import UndirectedGraphEngine, {NodeId as UNodeId,Edge as UEdge} from "../GraphEngine/UndirectedGraphEngine";
import DirectedGraphEngine, {NodeId as DNodeId, Edge as DEdge} from "../GraphEngine/DirectedGraphEngine";
import DijkstraEngine, {NodeId as WNodeId, WeightedEdge} from "../GraphEngine/DijkstraEngine";
import styles from "../Graph.module.css";

type NodeId = string;
type Edge = { from: NodeId; to: NodeId };

type TraversalType = "bfs" | "dfs" | null;
type PanelView = "list" | "matrix";

type PanelState = {
  graph: "Undirected" | "Directed" | "Weighted";
  view: PanelView;
};

const ALPHABET: NodeId[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function buildUndirectedAdjacency(
  nodes: NodeId[],
  edges: Edge[]
): Record<NodeId, NodeId[]> {
  const adj: Record<NodeId, NodeId[]> = {};
  nodes.forEach((n) => (adj[n] = []));
  edges.forEach(({ from, to }) => {
    if (!adj[from].includes(to)) adj[from].push(to);
    if (!adj[to].includes(from)) adj[to].push(from);
  });
  return adj;
}

function buildDirectedAdjacency(
  nodes: NodeId[],
  edges: Edge[]
): Record<NodeId, NodeId[]> {
  const adj: Record<NodeId, NodeId[]> = {};
  nodes.forEach((n) => (adj[n] = []));
  edges.forEach(({ from, to }) => {
    if (!adj[from].includes(to)) adj[from].push(to);
  });
  return adj;
}

function buildUndirectedMatrix(nodes: NodeId[], edges: Edge[]): number[][] {
  const index: Record<NodeId, number> = {};
  nodes.forEach((n, i) => (index[n] = i));
  const size = nodes.length;
  const matrix = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => 0)
  );
  edges.forEach(({ from, to }) => {
    const i = index[from];
    const j = index[to];
    if (i === undefined || j === undefined) return;
    matrix[i][j] = 1;
    matrix[j][i] = 1;
  });
  return matrix;
}

function buildDirectedMatrix(nodes: NodeId[], edges: Edge[]): number[][] {
  const index: Record<NodeId, number> = {};
  nodes.forEach((n, i) => (index[n] = i));
  const size = nodes.length;
  const matrix = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => 0)
  );
  edges.forEach(({ from, to }) => {
    const i = index[from];
    const j = index[to];
    if (i === undefined || j === undefined) return;
    matrix[i][j] = 1;
  });
  return matrix;
}

function buildWeightedAdjacency(
  nodes: NodeId[],
  edges: WeightedEdge[]
): Record<NodeId, Array<{ node: NodeId; weight: number }>> {
  const adj: Record<NodeId, Array<{ node: NodeId; weight: number }>> = {};
  nodes.forEach((n) => (adj[n] = []));
  edges.forEach(({ from, to, weight }) => {
    if (!adj[from]) adj[from] = [];
    if (!adj[to]) adj[to] = [];
    adj[from].push({ node: to, weight });
    adj[to].push({ node: from, weight });
  });
  return adj;
}

function buildWeightedMatrix(
  nodes: NodeId[],
  edges: WeightedEdge[]
): number[][] {
  const index: Record<NodeId, number> = {};
  nodes.forEach((n, i) => (index[n] = i));
  const size = nodes.length;
  const matrix: number[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => 0)
  );
  edges.forEach(({ from, to, weight }) => {
    const i = index[from];
    const j = index[to];
    if (i === undefined || j === undefined) return;
    matrix[i][j] = weight;
    matrix[j][i] = weight;
  });
  return matrix;
}

const CompareGraphsWrapper: React.FC = () => {
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<NodeId[]>([]);
  const [undirectedEdges, setUndirectedEdges] = useState<Edge[]>([]);
  const [directedEdges, setDirectedEdges] = useState<Edge[]>([]);
  const [weightedEdges, setWeightedEdges] = useState<WeightedEdge[]>([]);
  const [startNode, setStartNode] = useState<NodeId | null>(null);
  const [targetNode, setTargetNode] = useState<NodeId | null>(null);
  const [undirectedTraversal, setUndirectedTraversal] = useState<TraversalType>(null);
  const [undirectedResetSignal, setUndirectedResetSignal] = useState(0);
  const [undirectedHighlighted, setUndirectedHighlighted] = useState<NodeId[]>([]);  
  const [undirectedResult, setUndirectedResult] = useState<NodeId[]>([]);
  const [directedTraversal, setDirectedTraversal] = useState<TraversalType>(null);
  const [directedResetSignal, setDirectedResetSignal] = useState(0);
  const [directedHighlighted, setDirectedHighlighted] = useState<NodeId[]>([]);
  const [directedResult, setDirectedResult] = useState<NodeId[]>([]);
  const [dijkstraRunSignal, setDijkstraRunSignal] = useState(0);
  const [dijkstraResetSignal, setDijkstraResetSignal] = useState(0);
  const [dijkstraHighlighted, setDijkstraHighlighted] = useState<NodeId[]>([]);
  const [prev, setPrev] = useState<Record<NodeId, NodeId | null>>({});
  const [, setDistances] = useState<Record<NodeId, number>>({});
  const [, setQueueSnapshot] = useState<NodeId[]>([]);

  const [panelA, setPanelA] = useState<PanelState>({
    graph: "Undirected",
    view: "list",
  });
  const [panelB, setPanelB] = useState<PanelState>({
    graph: "Directed",
    view: "list",
  });
  const [panelC, setPanelC] = useState<PanelState>({
    graph: "Weighted",
    view: "list",
  });

  React.useEffect(() => {
    if (nodes.length === 0) {
      setStartNode(null);
      setTargetNode(null);
    } else {
      setStartNode((prev) =>
        prev && nodes.includes(prev) ? prev : nodes[0]
      );
      setTargetNode((prev) =>
        prev && nodes.includes(prev) ? prev : nodes[nodes.length - 1]
      );
    }
  }, [nodes]);

  const handleGenerateGraph = () => {
    const baseNodes = ALPHABET.slice(0, 5); // A–E
    const undirected: Edge[] = [];
    const weighted: WeightedEdge[] = [];

    for (let i = 0; i < baseNodes.length - 1; i++) {
      const from = baseNodes[i];
      const to = baseNodes[i + 1];
      const weight = 1 + Math.floor(Math.random() * 9);

      undirected.push({ from, to });
      weighted.push({ from, to, weight });
    }

    const possible: Array<[NodeId, NodeId]> = [];
    for (let i = 0; i < baseNodes.length; i++) {
      for (let j = i + 1; j < baseNodes.length; j++) {
        const from = baseNodes[i];
        const to = baseNodes[j];
        if (!undirected.some((e) =>
          (e.from === from && e.to === to) ||
          (e.from === to && e.to === from)
        )) {
          possible.push([from, to]);
        }
      }
    }

    const shuffled = [...possible].sort(() => Math.random() - 0.5);
    const extraCount = 2 + Math.floor(Math.random() * 2); // 2–3 extra
    const extraPairs = shuffled.slice(0, extraCount);

    for (const [from, to] of extraPairs) {
      const weight = 1 + Math.floor(Math.random() * 9);
      undirected.push({ from, to });
      weighted.push({ from, to, weight });
    }

    const directed: Edge[] = [];
    for (const { from, to } of undirected) {
      directed.push({ from, to });
    }
    setNodes(baseNodes);
    setUndirectedEdges(undirected);
    setWeightedEdges(weighted);
    setDirectedEdges(directed);
    setUndirectedTraversal(null);
    setDirectedTraversal(null);
    setUndirectedResetSignal((s) => s + 1);
    setDirectedResetSignal((s) => s + 1);
    setDijkstraResetSignal((s) => s + 1);
    setUndirectedHighlighted([]);
    setDirectedHighlighted([]);
    setDijkstraHighlighted([]);
    setUndirectedResult([]);
    setDirectedResult([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
  };

  const handleClearGraph = () => {
    setNodes([]);
    setUndirectedEdges([]);
    setDirectedEdges([]);
    setWeightedEdges([]);
    setUndirectedTraversal(null);
    setDirectedTraversal(null);
    setUndirectedResetSignal((s) => s + 1);
    setDirectedResetSignal((s) => s + 1);
    setDijkstraResetSignal((s) => s + 1);
    setUndirectedHighlighted([]);
    setDirectedHighlighted([]);
    setDijkstraHighlighted([]);
    setUndirectedResult([]);
    setDirectedResult([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
  };

  const handleRunBFS = () => {
    if (!startNode || nodes.length === 0) return;
    setUndirectedTraversal(null);
    setDirectedTraversal(null);
    setUndirectedResetSignal((s) => s + 1);
    setDirectedResetSignal((s) => s + 1);
    setUndirectedHighlighted([]);
    setDirectedHighlighted([]);
    setUndirectedResult([]);
    setDirectedResult([]);
    setTimeout(() => {
      setUndirectedTraversal("bfs");
      setDirectedTraversal("bfs");
    }, 0);
  };

  const handleRunDFS = () => {
    if (!startNode || nodes.length === 0) return;
    setUndirectedTraversal(null);
    setDirectedTraversal(null);
    setUndirectedResetSignal((s) => s + 1);
    setDirectedResetSignal((s) => s + 1);
    setUndirectedHighlighted([]);
    setDirectedHighlighted([]);
    setUndirectedResult([]);
    setDirectedResult([]);
    setTimeout(() => {
      setUndirectedTraversal("dfs");
      setDirectedTraversal("dfs");
    }, 0);
  };

  const handleRunDijkstra = () => {
    if (!startNode || nodes.length === 0) return;
    setDijkstraHighlighted([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
    setDijkstraRunSignal((s) => s + 1);
  };
  const handleResetAll = () => {
    setUndirectedTraversal(null);
    setDirectedTraversal(null);
    setUndirectedResetSignal((s) => s + 1);
    setDirectedResetSignal((s) => s + 1);
    setDijkstraResetSignal((s) => s + 1);
    setUndirectedHighlighted([]);
    setDirectedHighlighted([]);
    setDijkstraHighlighted([]);
    setUndirectedResult([]);
    setDirectedResult([]);
    setPrev({});
    setDistances({});
    setQueueSnapshot([]);
  };

  const shortestPathNodes: NodeId[] = useMemo(() => {
    if (!startNode || !targetNode || !prev || Object.keys(prev).length === 0) {
      return [];
    }

    const path: NodeId[] = [];
    let current: NodeId | null = targetNode;
    const guard = new Set<NodeId>();

    while (current !== null && !guard.has(current)) {
      guard.add(current);
      path.push(current);
      if (current === startNode) break;
      current = prev[current] ?? null;
    }

    if (path[path.length - 1] !== startNode) {
      return [];
    }
    return path.reverse();
  }, [startNode, targetNode, prev]);

  const undirectedAdjList = useMemo(
    () => buildUndirectedAdjacency(nodes, undirectedEdges),
    [nodes, undirectedEdges]
  );
  const directedAdjList = useMemo(
    () => buildDirectedAdjacency(nodes, directedEdges),
    [nodes, directedEdges]
  );
  const weightedAdjList = useMemo(
    () => buildWeightedAdjacency(nodes, weightedEdges),
    [nodes, weightedEdges]
  );

  const undirectedMatrix = useMemo(
    () => buildUndirectedMatrix(nodes, undirectedEdges),
    [nodes, undirectedEdges]
  );
  const directedMatrix = useMemo(
    () => buildDirectedMatrix(nodes, directedEdges),
    [nodes, directedEdges]
  );
  const weightedMatrix = useMemo(
    () => buildWeightedMatrix(nodes, weightedEdges),
    [nodes, weightedEdges]
  );

  const undirectedListText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    return nodes
      .map((n) => {
        const neighbors = undirectedAdjList[n] ?? [];
        return `${n}: ${neighbors.length ? neighbors.join(", ") : "—"}`;
      })
      .join("\n");
  }, [nodes, undirectedAdjList]);

  const directedListText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    return nodes
      .map((n) => {
        const neighbors = directedAdjList[n] ?? [];
        return `${n}: ${
          neighbors.length ? neighbors.map((m) => `→ ${m}`).join(", ") : "—"
        }`;
      })
      .join("\n");
  }, [nodes, directedAdjList]);

  const weightedListText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    return nodes
      .map((n) => {
        const neighbors = weightedAdjList[n] ?? [];
        const line = neighbors.length
          ? neighbors.map((e) => `${e.node}(${e.weight})`).join(", ")
          : "—";
        return `${n}: ${line}`;
      })
      .join("\n");
  }, [nodes, weightedAdjList]);

  const undirectedMatrixText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    const header = "   " + nodes.join(" ");
    const rows = undirectedMatrix.map((row, i) => {
      return `${nodes[i]}: ${row.join(" ")}`;
    });
    return [header, ...rows].join("\n");
  }, [nodes, undirectedMatrix]);

  const directedMatrixText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    const header = "   " + nodes.join(" ");
    const rows = directedMatrix.map((row, i) => {
      return `${nodes[i]}: ${row.join(" ")}`;
    });
    return [header, ...rows].join("\n");
  }, [nodes, directedMatrix]);

  const weightedMatrixText = useMemo(() => {
    if (nodes.length === 0) return "No nodes yet. Generate a graph first.";
    const header = "   " + nodes.join(" ");
    const rows = weightedMatrix.map((row, i) => {
      return `${nodes[i]}: ${row.join(" ")}`;
    });
    return [header, ...rows].join("\n");
  }, [nodes, weightedMatrix]);

  const getAdjacencyText = (graph: PanelState["graph"], view: PanelView) => {
    if (graph === "Undirected") {
      return view === "list" ? undirectedListText : undirectedMatrixText;
    }
    if (graph === "Directed") {
      return view === "list" ? directedListText : directedMatrixText;
    }
    return view === "list" ? weightedListText : weightedMatrixText;
  };

  return (
    <div className={styles.graphPageContainer}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        ← Return
      </button>

      <h1 className={styles.title}>Compare Graph Types</h1>
      <p className={styles.description}>
        Imagine exploring the same city using three different rulebooks.<br />
        An <strong>undirected graph</strong>  treats every road as two-way, letting you wander freely between locations.<br />
        A <strong>directed graph</strong>   turns those roads into one-way streets — suddenly, some paths exist only in theory, and others become dead ends.<br />
        A <strong>weighted graph</strong>  adds travel time to every road, where the shortest route isn’t the one with the fewest steps, but the one with the least cost.<br />
        Run BFS, DFS, or Dijkstra on these views and you'll see the same graph behave in completely different ways depending on direction and weight.
        Same nodes, same edges, same layout — but different rules reshape the routes you discover, the paths that are valid, and the algorithm that gets you there fastest.
      </p>

      <div className={styles.graphControlsRow}>
        <div className={styles.graphControlsGroup}>
          <h3>Build Shared Graph</h3>
          <button onClick={handleGenerateGraph}>Generate Graph (A–E)</button>
          <button onClick={handleClearGraph}>Clear Graph</button>

          <p className={styles.controlsNote}>
            This generates a single 5-node graph (A–E) with a connected base
            structure and a few random extra edges. The{" "}
            <strong>undirected</strong>, <strong>directed</strong>, and{" "}
            <strong>weighted</strong> views all use this same underlying
            structure.
          </p>
        </div>

        <div className={styles.graphControlsGroup}>
          <h3>Traversals &amp; Shortest Path</h3>

          <div className={styles.inlineForm}>
            <label>
              Start Node:
              <select
                value={startNode ?? ""}
                onChange={(e) => setStartNode(e.target.value as NodeId)}
                disabled={nodes.length === 0}
              >
                {nodes.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Dijkstra Target:
              <select
                value={targetNode ?? ""}
                onChange={(e) => setTargetNode(e.target.value as NodeId)}
                disabled={nodes.length === 0}
              >
                {nodes.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className={styles.inlineForm}>
            <button onClick={handleRunBFS} disabled={nodes.length === 0}>
              Run BFS (Undirected &amp; Directed)
            </button>
            <button onClick={handleRunDFS} disabled={nodes.length === 0}>
              Run DFS (Undirected &amp; Directed)
            </button>
            <button onClick={handleRunDijkstra} disabled={nodes.length === 0}>
              Run Dijkstra (Weighted)
            </button>
            <button onClick={handleResetAll}>Reset All</button>
          </div>

          <p className={styles.controlsNote}>
            BFS and DFS run in parallel on the{" "}
            <strong>undirected</strong> and <strong>directed</strong> views
            using the same start node. Dijkstra runs on the{" "}
            <strong>weighted</strong> view and highlights the shortest path
            from the start to the selected target node.
          </p>

          <p className={styles.traversalResult}>
            Undirected traversal result:{" "}
            {undirectedResult.length
              ? undirectedResult.join(" → ")
              : "No traversal yet."}
          </p>
          <p className={styles.traversalResult}>
            Directed traversal result:{" "}
            {directedResult.length
              ? directedResult.join(" → ")
              : "No traversal yet."}
          </p>
          <p className={styles.traversalResult}>
            Dijkstra shortest path from {startNode ?? "—"} to{" "}
            {targetNode ?? "—"}:{" "}
            {shortestPathNodes.length
              ? shortestPathNodes.join(" → ")
              : "No path yet or Dijkstra not run."}
          </p>
        </div>
      </div>

      <div className={styles.compareGraphsGrid}>
        <div className={styles.compareGraphBox}>
          <h3>Undirected Graph</h3>
          <UndirectedGraphEngine
            nodes={nodes as UNodeId[]}
            edges={undirectedEdges as UEdge[]}
            traversal={undirectedTraversal}
            startNode={startNode as UNodeId | null}
            resetSignal={undirectedResetSignal}
            highlightedNodes={undirectedHighlighted}
            setHighlightedNodes={setUndirectedHighlighted}
            setTraversalResult={setUndirectedResult}
          />
        </div>

        <div className={styles.compareGraphBox}>
          <h3>Directed Graph</h3>
          <DirectedGraphEngine
            nodes={nodes as DNodeId[]}
            edges={directedEdges as DEdge[]}
            traversal={directedTraversal}
            startNode={startNode as DNodeId | null}
            resetSignal={directedResetSignal}
            highlightedNodes={directedHighlighted}
            setHighlightedNodes={setDirectedHighlighted}
            setTraversalResult={setDirectedResult}
          />
        </div>

        <div className={styles.compareGraphBox}>
          <h3>Weighted Graph (Dijkstra)</h3>
          <DijkstraEngine
            nodes={nodes as WNodeId[]}
            edges={weightedEdges}
            startNode={startNode as WNodeId | null}
            runSignal={dijkstraRunSignal}
            resetSignal={dijkstraResetSignal}
            highlightedNodes={dijkstraHighlighted}
            shortestPathNodes={shortestPathNodes}
            setHighlightedNodes={setDijkstraHighlighted}
            setDistances={setDistances}
            setPrev={setPrev}
            setQueueSnapshot={setQueueSnapshot}
          />
        </div>
      </div>

      <p className={styles.description}>
        Each panel can display the <strong>adjacency list</strong> or{" "}
        <strong>adjacency matrix</strong> for any of the three graph views.
        Use these to compare how <strong>direction</strong> and{" "}
        <strong>weights</strong> change the underlying representation.
      </p>

      <div className={styles.adjCompareRow}>
        <div className={styles.adjPanel}>
          <div className={styles.inlineForm}>
            <label>
              Graph:
              <select
                value={panelB.graph}
                onChange={(e) =>
                  setPanelB({
                    ...panelB,
                    graph: e.target.value as PanelState["graph"],
                  })
                }
              >
                <option value="Undirected">Undirected</option>
                <option value="Directed">Directed</option>
                <option value="Weighted">Weighted</option>
              </select>
            </label>

            <label>
              View:
              <select
                value={panelB.view}
                onChange={(e) =>
                  setPanelB({
                    ...panelB,
                    view: e.target.value as PanelView,
                  })
                }
              >
                <option value="list">Adjacency List</option>
                <option value="matrix">Adjacency Matrix</option>
              </select>
            </label>
          </div>

          <pre className={styles.codeBlock}>
            {getAdjacencyText(panelB.graph, panelB.view)}
          </pre>
        </div>

        {/* Panel C */}
        <div className={styles.adjPanel}>
          <div className={styles.inlineForm}>
            <label>
              Graph:
              <select
                value={panelC.graph}
                onChange={(e) =>
                  setPanelC({
                    ...panelC,
                    graph: e.target.value as PanelState["graph"],
                  })
                }
              >
                <option value="Undirected">Undirected</option>
                <option value="Directed">Directed</option>
                <option value="Weighted">Weighted</option>
              </select>
            </label>

            <label>
              View:
              <select
                value={panelC.view}
                onChange={(e) =>
                  setPanelC({
                    ...panelC,
                    view: e.target.value as PanelView,
                  })
                }
              >
                <option value="list">Adjacency List</option>
                <option value="matrix">Adjacency Matrix</option>
              </select>
            </label>
          </div>

          <pre className={styles.codeBlock}>
            {getAdjacencyText(panelC.graph, panelC.view)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default CompareGraphsWrapper;
